import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  Timestamp,
  where,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type {
  AiVerificationAssignmentSet,
  AiVerificationDistribution,
  AiVerificationGenerationConstraints,
  AiVerificationQuestion,
  AiVerificationRandomConstraints,
  AiVerificationSummary,
  AiVerificationSummaryBucket,
  AiVerificationTally,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * Sprint X: Verification 結果閲覧 hook (admin)。
 *
 * 既存 user 側 `useAssignmentSets` (apps/user) と異なり、admin では status 問わず全 set を一覧表示する
 * (draft / published / closed)。subscribe による reactive 更新。
 *
 * Set 選択後は、`fetchVerificationResults` で各 candidate post の verificationSummary を一括取得する
 * (Promise.all で並列、Phase 5 minimum 規模 = 数十件想定で十分)。
 */

// ============================================================
// AssignmentSets 全件 subscribe
// ============================================================

export type AssignmentSetsAdminState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: AiVerificationAssignmentSet[]; error: null }
  | { status: 'error'; items: []; error: Error };

function tsToIso(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate(): Date }).toDate().toISOString();
    } catch {
      return new Date().toISOString();
    }
  }
  return new Date().toISOString();
}

function asDistribution(value: unknown): AiVerificationDistribution {
  if (
    value === 'all_same' ||
    value === 'random_per_user' ||
    value === 'manual' ||
    value === 'free'
  ) {
    return value;
  }
  return 'all_same';
}

function asRandomConstraints(value: unknown): AiVerificationRandomConstraints | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const v = value as Record<string, unknown>;
  if (typeof v.minPostsPerUser !== 'number') return undefined;
  return {
    minPostsPerUser: v.minPostsPerUser,
    minOwnPosts: typeof v.minOwnPosts === 'number' ? v.minOwnPosts : 0,
    allowDuplicates: typeof v.allowDuplicates === 'boolean' ? v.allowDuplicates : false,
    preferOwnPosts: typeof v.preferOwnPosts === 'boolean' ? v.preferOwnPosts : true,
    preferLessVerified: typeof v.preferLessVerified === 'boolean' ? v.preferLessVerified : false,
  };
}

/** generationConstraints (生成時制約) の defensive narrow。object でなければ undefined。 */
function asGenerationConstraints(value: unknown): AiVerificationGenerationConstraints | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const v = value as Record<string, unknown>;
  const out: AiVerificationGenerationConstraints = { excludeOwnPosts: v.excludeOwnPosts === true };
  if (typeof v.maxPerUser === 'number') out.maxPerUser = v.maxPerUser;
  if (typeof v.minOwnPosts === 'number') out.minOwnPosts = v.minOwnPosts;
  if (typeof v.preferOwnPosts === 'boolean') out.preferOwnPosts = v.preferOwnPosts;
  if (typeof v.sameSessionOnly === 'boolean') out.sameSessionOnly = v.sameSessionOnly;
  if (
    v.strategyOriginal === 'random_per_user' ||
    v.strategyOriginal === 'round_robin' ||
    v.strategyOriginal === 'all_users'
  ) {
    out.strategyOriginal = v.strategyOriginal;
  }
  return out;
}

export function mapAssignmentSet(snap: {
  id: string;
  data: () => DocumentData;
}): AiVerificationAssignmentSet {
  const data = snap.data();
  const out: AiVerificationAssignmentSet = {
    id: snap.id,
    displayName: typeof data.displayName === 'string' ? data.displayName : '',
    description: typeof data.description === 'string' ? data.description : '',
    templateId: typeof data.templateId === 'string' ? data.templateId : '',
    templateVersion: typeof data.templateVersion === 'number' ? data.templateVersion : 0,
    distribution: asDistribution(data.distribution),
    candidatePostIds: Array.isArray(data.candidatePostIds)
      ? data.candidatePostIds.filter((x): x is string => typeof x === 'string')
      : [],
    startAt:
      data.startAt === null ? null : data.startAt === undefined ? null : tsToIso(data.startAt),
    endAt: data.endAt === null ? null : data.endAt === undefined ? null : tsToIso(data.endAt),
    status: data.status === 'published' || data.status === 'closed' ? data.status : 'draft',
    createdAt: tsToIso(data.createdAt),
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    updatedAt: tsToIso(data.updatedAt),
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : '',
  };
  const rc = asRandomConstraints(data.randomConstraints);
  if (rc) out.randomConstraints = rc;
  if (Array.isArray(data.sessionIds)) {
    out.sessionIds = data.sessionIds.filter((x): x is string => typeof x === 'string');
  }
  if (data.sessionId !== undefined) {
    out.sessionId = typeof data.sessionId === 'string' ? data.sessionId : null;
  }
  // 生成メタ (2026-09-04): 予約 (plan) 由来 / 対象セッション / 生成状態 / 生成時制約の表示分岐に使う。
  const gc = asGenerationConstraints(data.generationConstraints);
  if (gc) out.generationConstraints = gc;
  if (data.sourcePlanId !== undefined) {
    out.sourcePlanId = typeof data.sourcePlanId === 'string' ? data.sourcePlanId : null;
  }
  if (data.sourceSessionId !== undefined) {
    out.sourceSessionId = typeof data.sourceSessionId === 'string' ? data.sourceSessionId : null;
  }
  if (
    data.generationState === 'running' ||
    data.generationState === 'complete' ||
    data.generationState === 'failed'
  ) {
    out.generationState = data.generationState;
  }
  return out;
}

/** 地区の全 AssignmentSet を published 優先で並べて subscribe。 */
export function useAdminAssignmentSets(districtId: string | null): AssignmentSetsAdminState {
  const [state, setState] = useState<AssignmentSetsAdminState>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    if (!districtId) {
      setState({ status: 'error', items: [], error: new Error('districtId is required') });
      return;
    }
    // admin は draft/published/closed 全件閲覧可、where 無しで全件取得
    const q = collection(db, 'districts', districtId, 'aiVerificationAssignmentSets');
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map(mapAssignmentSet);
        // published を先頭に、それ以外は updatedAt desc
        items.sort((a, b) => {
          if (a.status !== b.status) {
            if (a.status === 'published') return -1;
            if (b.status === 'published') return 1;
          }
          return b.updatedAt.localeCompare(a.updatedAt);
        });
        setState({ status: 'ready', items, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return () => unsub();
  }, [districtId]);

  return state;
}

// ============================================================
// 投稿 + verificationSummary 一括取得
// ============================================================

export interface VerificationResultPost {
  id: string;
  title: string;
  authorNickname: string;
  /** AW4 (docs/108 #14/#7) — 個票モーダルの「自他」判定 + サマリ CSV の投稿者UID 列に使用。 */
  authorUid: string;
  summary: AiVerificationSummary | null;
}

function asTally(value: unknown): AiVerificationTally {
  if (!value || typeof value !== 'object') return {};
  const v = value as Record<string, unknown>;
  const out: AiVerificationTally = {};
  if (typeof v.valid === 'number') out.valid = v.valid;
  if (typeof v.neutral === 'number') out.neutral = v.neutral;
  if (typeof v.invalid === 'number') out.invalid = v.invalid;
  if (typeof v.yes === 'number') out.yes = v.yes;
  if (typeof v.no === 'number') out.no = v.no;
  if (typeof v.scaleSum === 'number') out.scaleSum = v.scaleSum;
  if (typeof v.scaleCount === 'number') out.scaleCount = v.scaleCount;
  if (typeof v.textCount === 'number') out.textCount = v.textCount;
  if (v.choiceCounts && typeof v.choiceCounts === 'object') {
    const cc: Record<string, number> = {};
    for (const [k, n] of Object.entries(v.choiceCounts as Record<string, unknown>)) {
      if (typeof n === 'number') cc[k] = n;
    }
    out.choiceCounts = cc;
  }
  return out;
}

function asTallyMap(value: unknown): Record<string, AiVerificationTally> {
  if (!value || typeof value !== 'object') return {};
  const out: Record<string, AiVerificationTally> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = asTally(v);
  }
  return out;
}

function asPerClassification(value: unknown): Record<string, Record<string, AiVerificationTally>> {
  if (!value || typeof value !== 'object') return {};
  const out: Record<string, Record<string, AiVerificationTally>> = {};
  for (const [rank, qmap] of Object.entries(value as Record<string, unknown>)) {
    out[rank] = asTallyMap(qmap);
  }
  return out;
}

/** 集計 1 バケット (全体 / 自分の投稿への回答 / 他の人の投稿への回答) の defensive narrow。 */
function asSummaryBucket(value: unknown): AiVerificationSummaryBucket | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  return {
    totalResponses: typeof v.totalResponses === 'number' ? v.totalResponses : 0,
    completedResponses: typeof v.completedResponses === 'number' ? v.completedResponses : 0,
    perPost: asTallyMap(v.perPost),
    perClassification: asPerClassification(v.perClassification),
  };
}

/** Firestore raw → AiVerificationSummary (defensive narrow)。 */
export function mapVerificationSummary(value: unknown): AiVerificationSummary | null {
  const whole = asSummaryBucket(value);
  if (!whole) return null;
  const v = value as Record<string, unknown>;
  const out: AiVerificationSummary = {
    ...whole,
    lastAggregatedAt: typeof v.lastAggregatedAt === 'string' ? v.lastAggregatedAt : '',
  };
  // 自分の投稿への回答 / 他の人の投稿への回答 の分離集計 (2026-09-04)。無い (旧 summary) なら未設定のまま。
  const own = asSummaryBucket(v.own);
  if (own) out.own = own;
  const others = asSummaryBucket(v.others);
  if (others) out.others = others;
  return out;
}

/**
 * candidatePostIds 配列を解決 → 各 post の (title / nickname / verificationSummary) を Promise.all で取得。
 *
 * Firestore の制約: in 句は 10 件まで → 単純に並列 getDoc で済ませる (Phase 5 minimum 規模)。
 */
export async function fetchVerificationResults(
  districtId: string,
  postIds: string[],
): Promise<VerificationResultPost[]> {
  if (postIds.length === 0) return [];
  const results = await Promise.all(
    postIds.map(async (postId) => {
      const ref = doc(db, 'districts', districtId, 'posts', postId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        return {
          id: postId,
          title: '(削除済 / 存在しません)',
          authorNickname: '',
          authorUid: '',
          summary: null,
        };
      }
      const data = snap.data();
      return {
        id: postId,
        title: typeof data.title === 'string' ? data.title : '(無題)',
        authorNickname: typeof data.authorNickname === 'string' ? data.authorNickname : '',
        authorUid: typeof data.authorUid === 'string' ? data.authorUid : '',
        summary: mapVerificationSummary(data.verificationSummary),
      } satisfies VerificationResultPost;
    }),
  );
  return results;
}

/**
 * 地区内の全投稿のうち verificationSummary を持つものを取得する (AW4 #7、横断サマリ用)。
 * set 未選択でも「地区全体の投稿サマリ」を出せるモードのデータソース。
 * archivedAt 付き / summary 無しは除外。並べ替えは呼び出し側に委ねる。
 */
export async function fetchAllPostsWithSummary(
  districtId: string,
): Promise<VerificationResultPost[]> {
  const snap = await getDocs(
    query(collection(db, 'districts', districtId, 'posts'), where('archivedAt', '==', null)),
  );
  const out: VerificationResultPost[] = [];
  for (const d of snap.docs) {
    const data = d.data();
    const summary = mapVerificationSummary(data.verificationSummary);
    // summary が無い / 回答ゼロの投稿は横断サマリから除外
    if (!summary || (summary.totalResponses === 0 && summary.completedResponses === 0)) {
      continue;
    }
    out.push({
      id: d.id,
      title: typeof data.title === 'string' ? data.title : '(無題)',
      authorNickname: typeof data.authorNickname === 'string' ? data.authorNickname : '',
      authorUid: typeof data.authorUid === 'string' ? data.authorUid : '',
      summary,
    });
  }
  return out;
}

/**
 * AssignmentSet 指定で結果一覧を hook 化。
 * setId が null の間は idle、set 確定後 + candidatePostIds が解決され次第 fetch。
 */
export type VerificationResultsState =
  | { status: 'idle'; posts: []; error: null }
  | { status: 'loading'; posts: []; error: null }
  | { status: 'ready'; posts: VerificationResultPost[]; error: null }
  | { status: 'error'; posts: []; error: Error };

export function useVerificationResults(
  districtId: string | null,
  set: AiVerificationAssignmentSet | null,
): VerificationResultsState {
  const [state, setState] = useState<VerificationResultsState>({
    status: 'idle',
    posts: [],
    error: null,
  });

  useEffect(() => {
    if (!districtId || !set) {
      setState({ status: 'idle', posts: [], error: null });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading', posts: [], error: null });
    fetchVerificationResults(districtId, set.candidatePostIds)
      .then((posts) => {
        if (cancelled) return;
        setState({ status: 'ready', posts, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ status: 'error', posts: [], error: err as Error });
      });
    return () => {
      cancelled = true;
    };
    // set 更新時 (status 変更 / candidatePostIds 変更) も再 fetch
  }, [districtId, set?.id, set?.candidatePostIds.join('|'), set?.updatedAt]);

  return state;
}

// ============================================================
// テンプレート 1 件 fetch (questionId → type 解決用)
// ============================================================

/** template doc から questionId → type / label を map 化する pure helper。 */
export interface QuestionMeta {
  id: string;
  type: string;
  label: string;
  /** perPost / perClassification の区別。 */
  scope: 'perPost' | 'perClassification';
}

export async function fetchVerificationTemplateQuestions(
  districtId: string,
  templateId: string,
): Promise<QuestionMeta[]> {
  const ref = doc(db, 'districts', districtId, 'aiVerificationTemplates', templateId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return [];
  const data = snap.data();
  const out: QuestionMeta[] = [];
  for (const q of Array.isArray(data.perPostQuestions) ? data.perPostQuestions : []) {
    if (q && typeof q === 'object' && typeof (q as { id?: unknown }).id === 'string') {
      out.push({
        id: (q as { id: string }).id,
        type:
          typeof (q as { type?: unknown }).type === 'string'
            ? (q as { type: string }).type
            : 'three_state',
        label:
          typeof (q as { label?: unknown }).label === 'string'
            ? (q as { label: string }).label
            : '',
        scope: 'perPost',
      });
    }
  }
  for (const q of Array.isArray(data.perClassificationQuestions)
    ? data.perClassificationQuestions
    : []) {
    if (q && typeof q === 'object' && typeof (q as { id?: unknown }).id === 'string') {
      out.push({
        id: (q as { id: string }).id,
        type:
          typeof (q as { type?: unknown }).type === 'string'
            ? (q as { type: string }).type
            : 'three_state',
        label:
          typeof (q as { label?: unknown }).label === 'string'
            ? (q as { label: string }).label
            : '',
        scope: 'perClassification',
      });
    }
  }
  return out;
}

// ============================================================
// テンプレート 1 件 fetch (full question 配列、個票モーダル用 — AW4 #14)
// ============================================================

function narrowQuestions(raw: unknown): AiVerificationQuestion[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (q): q is AiVerificationQuestion =>
      typeof q === 'object' && q !== null && typeof (q as { id?: unknown }).id === 'string',
  );
}

export interface VerificationTemplateFull {
  id: string;
  displayName: string;
  perPostQuestions: AiVerificationQuestion[];
  perClassificationQuestions: AiVerificationQuestion[];
}

/**
 * 個票モーダル用に、テンプレ doc から enabled な full question 配列を取得する。
 * choices / helpText / type を含むため、AnswerCell の選択肢ラベル解決まで賄える。
 */
export async function fetchVerificationTemplateFull(
  districtId: string,
  templateId: string,
): Promise<VerificationTemplateFull | null> {
  const ref = doc(db, 'districts', districtId, 'aiVerificationTemplates', templateId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    id: snap.id,
    displayName: typeof data.displayName === 'string' ? data.displayName : '',
    perPostQuestions: narrowQuestions(data.perPostQuestions),
    perClassificationQuestions: narrowQuestions(data.perClassificationQuestions),
  };
}
